import { useMemo } from 'react';

import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';

import BankOfAmericaRequestedStatus from '#client/components/OpenAccountApplication/components/Steps/ValidationMessage/components/BankOfAmericaRequestedStatus';
import BoAModal from '#client/components/OpenAccountApplication/components/Steps/ValidationMessage/components/BoAModal';
import { PRODUCT_CATALOG_SERVICES } from '#client/constants/catalogServices';
import { BANK_OF_AMERICA_OFFER_URL, MERCURY_REFERRAL_URL } from '#client/constants/url';
import useModalContext from '#client/hooks/useModalContext';
import useOrderTaskContext from '#client/hooks/useOrderTaskContext';
import { findLatestOrderTaskByServiceName } from '#client/utils/orderTasks';

/**
 * Builds the two partner cards, each with its CTA and clickstream. Shared by the transition
 * flow and the Business Banking Accounts landing page, which track under different names.
 * @param {object} clickstream Page-specific tracking metadata
 * @param {string} clickstream.appPageName Clickstream page name
 * @param {object} clickstream.elementNames Element name per CTA
 * @returns {Array<object>} Cards in display order, shaped for `OfferCard`
 */
export default function useBankingOptionCards({ appPageName, elementNames }) {
  const { orderTasks } = useOrderTaskContext();
  const { openModal } = useModalContext();

  const bankOfAmericaOffer = findLatestOrderTaskByServiceName(
    orderTasks,
    PRODUCT_CATALOG_SERVICES.BANK_OF_AMERICA_OFFER
  );
  const hasRequestedBankOfAmerica = Boolean(bankOfAmericaOffer?.type);

  const mercuryButtonPressed = useButtonPressedCallback({
    elementText: 'Get started',
    elementName: elementNames.mercuryCta,
    appPageName
  });

  const bankOfAmericaOfferPageLinkPressed = useLinkPressedCallback({
    elementText: '* See offer page for details',
    elementName: elementNames.bankOfAmericaOfferLink,
    appPageName
  });

  const bankOfAmericaButtonPressed = useButtonPressedCallback({
    elementText: 'Contact me',
    elementName: elementNames.bankOfAmericaCta,
    appPageName
  });

  const mercuryCard = useMemo(
    () => ({
      id: 'mercury-card',
      isRecommended: true,
      logo: { src: '/s2/vector/mercury-logo.svg', alt: 'Mercury', width: 235 },
      title: 'Business Banking account*',
      description: 'Manage your finances with powerful tools and earn a $150 bonus.**',
      features: [
        '$150 cash bonus offer**',
        'No account opening or overdraft fees',
        'Free ACH and USD wire transfers',
        'Built-in bill pay, invoicing, and accounting integrations'
      ],
      ctaButton: {
        text: 'Get started',
        onClick: () => {
          mercuryButtonPressed();
          window.open(MERCURY_REFERRAL_URL, '_blank', 'noopener,noreferrer');
        }
      }
    }),
    [mercuryButtonPressed]
  );

  const bankOfAmericaCard = useMemo(
    () => ({
      id: 'bank-of-america-card',
      logo: { src: '/logo/Bank-of-America-Logo.svg', alt: 'Bank of America', width: 224 },
      title: 'Business Advantage Banking',
      description: 'Earn bonuses totaling $300 to $800 with qualifying activities.',
      features: [
        '$300 checking bonus offer*',
        '$500 credit card cash rewards bonus offer*',
        'Separate your business and personal accounts',
        'Track business expenses more easily'
      ],
      disclaimer: {
        anchorText: '* See offer page for details',
        hyperlink: BANK_OF_AMERICA_OFFER_URL,
        onClick: bankOfAmericaOfferPageLinkPressed
      },
      disclosure: 'Deposit products are provided by Bank of America, N.A. Member FDIC.',
      // Opting in replaces the CTA, so the customer stays on the page and may still choose Mercury.
      ...(hasRequestedBankOfAmerica
        ? { ctaSlot: <BankOfAmericaRequestedStatus /> }
        : {
            ctaButton: {
              text: 'Contact me',
              onClick: () => {
                bankOfAmericaButtonPressed();
                openModal({ name: 'BoA Modal', component: BoAModal, isOpen: true });
              }
            }
          })
    }),
    [
      bankOfAmericaButtonPressed,
      bankOfAmericaOfferPageLinkPressed,
      hasRequestedBankOfAmerica,
      openModal
    ]
  );

  return [mercuryCard, bankOfAmericaCard];
}
